import React, { useEffect, useMemo, useState } from "react";
import Layout from "../Partials/Layout";
import ModalCom from "../Helpers/ModalCom";
import HealthTrendChart from "../Charts/HealthTrendChart";
import usersService from "../../services/UsersService";

// Same capabilities as myfit-app's Health Track feature (HealthTrackScreen/
// AddHealthRecordScreen/HealthRecordDetailScreen), against the same
// backend (myfit-api) — replaces the old vendor-desktop-API "track
// category" flow, which depended on `member_id` (a value the passwordless
// myfit-api login has no equivalent for — see Login/index.jsx).
const MIN_ENTRIES_FOR_TREND = 5;
const MAX_TREND_ENTRIES = 30;

function pad(n) {
  return String(n).padStart(2, "0");
}

// "YYYY-MM-DDTHH:mm", the exact value <input type="datetime-local"> both
// reads and writes — no Date round-tripping needed.
function nowAsDatetimeLocalValue() {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(
    now.getHours()
  )}:${pad(now.getMinutes())}`;
}

// myfit-api's recorded_at accepts datetime.fromisoformat() — the
// datetime-local value is already that shape, just missing seconds.
function toRecordedAt(datetimeLocalValue) {
  if (!datetimeLocalValue) return undefined;
  return datetimeLocalValue.length === 16 ? `${datetimeLocalValue}:00` : datetimeLocalValue;
}

function formatValues(record, type) {
  if (!type) return Object.values(record.values).join(" / ");
  return type.fields.map((f) => record.values[f.id]).join("/");
}

function formatWhen(iso) {
  const when = new Date(iso);
  return `${when.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  })} · ${when.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

function AddHealthRecordModal({ types, unitsPref, onClose, onSaved }) {
  const api = new usersService();
  const [selectedTypeId, setSelectedTypeId] = useState(types[0]?.id || "");
  const [fieldValues, setFieldValues] = useState({});
  const [when, setWhen] = useState(nowAsDatetimeLocalValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectedType = types.find((ty) => ty.id === selectedTypeId) || null;

  const onSelectType = (id) => {
    setSelectedTypeId(id);
    setFieldValues({});
  };

  const onChangeField = (fieldId, value) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    setFieldValues((prev) => ({ ...prev, [fieldId]: cleaned }));
  };

  const onSave = async () => {
    if (!selectedType) {
      setError("Choose a tracking type.");
      return;
    }
    const values = {};
    for (let i = 0; i < selectedType.fields.length; i += 1) {
      const field = selectedType.fields[i];
      const raw = fieldValues[field.id];
      const n = raw ? parseFloat(raw) : NaN;
      if (Number.isNaN(n) || n <= 0) {
        setError(`Enter a positive number for ${field.label}.`);
        return;
      }
      values[field.id] = n;
    }

    setError("");
    setSaving(true);
    try {
      const res = await api.addHealthRecord({
        type: selectedType.id,
        values,
        recorded_at: toRecordedAt(when),
      });
      onSaved(res.data.record);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save this reading.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="logout-modal-wrapper lg:w-[520px] h-full lg:max-h-[90vh] overflow-y-auto bg-white dark:bg-dark-white lg:rounded-2xl">
      <div className="logout-modal-header w-full flex items-center justify-between lg:px-10 lg:py-8 px-[30px] py-[23px] border-b border-light-purple dark:border-[#5356fb29] ">
        <h1 className="text-26 font-bold text-dark-gray dark:text-white tracking-wide">
          Add Reading
        </h1>
        <button
          type="button"
          onClick={onClose}
          className="text-3xl leading-none text-thin-light-gray"
        >
          ×
        </button>
      </div>
      <div className="logout-modal-body w-full px-10 py-8">
        <p className="text-sm text-thin-light-gray mb-2">Type</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {types.map((ty) => (
            <button
              key={ty.id}
              type="button"
              onClick={() => onSelectType(ty.id)}
              disabled={saving}
              className={`px-4 py-2 rounded-full border text-sm font-semibold ${
                selectedTypeId === ty.id
                  ? "bg-purple text-white border-purple"
                  : "border-light-purple dark:border-[#5356fb29] text-dark-gray dark:text-white"
              }`}
            >
              {ty.label} ({ty.unit[unitsPref]})
            </button>
          ))}
        </div>

        {selectedType && (
          <div className="flex flex-wrap gap-4 mb-6">
            {selectedType.fields.map((field) => (
              <div key={field.id} className="flex-1 min-w-[120px]">
                <label className="text-sm text-thin-light-gray block mb-2" htmlFor={field.id}>
                  {field.label} ({selectedType.unit[unitsPref]})
                </label>
                <input
                  id={field.id}
                  type="text"
                  inputMode="decimal"
                  value={fieldValues[field.id] || ""}
                  onChange={(e) => onChangeField(field.id, e.target.value)}
                  disabled={saving}
                  placeholder="0"
                  className="w-full border border-light-purple dark:border-[#5356fb29] rounded-full px-5 py-3 bg-[#FAFAFA] dark:bg-[#11131F] text-dark-gray dark:text-white focus:ring-0 focus:outline-none"
                />
              </div>
            ))}
          </div>
        )}

        <p className="text-sm text-thin-light-gray mb-2">Date &amp; time</p>
        <input
          type="datetime-local"
          value={when}
          max={nowAsDatetimeLocalValue()}
          onChange={(e) => setWhen(e.target.value)}
          disabled={saving}
          className="w-full border border-light-purple dark:border-[#5356fb29] rounded-full px-5 py-3 bg-[#FAFAFA] dark:bg-[#11131F] text-dark-gray dark:text-white mb-6 focus:ring-0 focus:outline-none"
        />

        {error && <p className="text-center text-red-700 mb-4">{error}</p>}

        <div className="flex justify-center">
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="btn-login rounded-[50px] text-xl text-white font-bold px-10 py-3 bg-purple"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function HealthRecordDetailModal({ record, type, allRecordsOfType, onClose, onDeleted }) {
  const api = new usersService();
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState("");

  const typeLabel = type ? type.label : record.type;
  const fields = type ? type.fields : Object.keys(record.values).map((id) => ({ id, label: id }));

  // allRecordsOfType comes back newest-first (same /health-records list the
  // page already loaded) — take the most recent 30 and flip to oldest-first
  // so the chart reads left-to-right, matching myfit-app.
  const showTrend = allRecordsOfType.length > MIN_ENTRIES_FOR_TREND;
  const trendRecords = showTrend
    ? [...allRecordsOfType].slice(0, MAX_TREND_ENTRIES).reverse()
    : [];

  const onDelete = async () => {
    setError("");
    setDeleting(true);
    try {
      await api.deleteHealthRecord(record.id);
      onDeleted(record.id);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not delete this reading.");
      setDeleting(false);
    }
  };

  return (
    <div className="logout-modal-wrapper lg:w-[520px] h-full lg:max-h-[90vh] overflow-y-auto bg-white dark:bg-dark-white   lg:rounded-2xl">
      <div className="logout-modal-header w-full flex items-center justify-between lg:px-10 lg:py-8 px-[30px] py-[23px] border-b border-light-purple dark:border-[#5356fb29] ">
        <h1 className="text-26 font-bold text-dark-gray dark:text-white tracking-wide">
          {typeLabel}
        </h1>
        <button
          type="button"
          onClick={onClose}
          className="text-3xl leading-none text-thin-light-gray"
        >
          ×
        </button>
      </div>
      <div className="logout-modal-body w-full px-10 py-8">
        <div className="mb-6">
          <div className="flex justify-between py-3 border-b border-light-purple dark:border-[#5356fb29]">
            <span className="text-thin-light-gray">Date &amp; time</span>
            <span className="text-dark-gray dark:text-white font-medium">
              {formatWhen(record.recorded_at)}
            </span>
          </div>
          {fields.map((field, i) => (
            <div
              key={field.id}
              className={`flex justify-between py-3 ${
                i === fields.length - 1 ? "" : "border-b border-light-purple dark:border-[#5356fb29]"
              }`}
            >
              <span className="text-thin-light-gray">{field.label}</span>
              <span className="text-dark-gray dark:text-white font-medium">
                {record.values[field.id]} {record.unit}
              </span>
            </div>
          ))}
        </div>

        {showTrend && (
          <div className="mb-6">
            <p className="text-sm text-thin-light-gray mb-2">
              Last {trendRecords.length} readings
            </p>
            <HealthTrendChart records={trendRecords} type={type} />
          </div>
        )}

        {error && <p className="text-center text-red-700 mb-4">{error}</p>}

        {confirmingDelete ? (
          <div className="flex justify-center space-x-3">
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="text-white bg-light-red text-base font-bold px-6 py-3 rounded-full"
            >
              {deleting ? "Deleting…" : "Yes, Delete"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
              className="border-gradient text-base px-6 py-3 rounded-full"
            >
              <span className="text-gradient">Cancel</span>
            </button>
          </div>
        ) : (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="text-white bg-light-red text-base font-bold px-6 py-3 rounded-full"
            >
              Delete Reading
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Tracking() {
  const api = new usersService();

  const [types, setTypes] = useState([]);
  const [records, setRecords] = useState([]);
  const [unitsPref, setUnitsPref] = useState("metric");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showAddModal, setShowAddModal] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  const typesById = useMemo(() => {
    const map = {};
    types.forEach((ty) => {
      map[ty.id] = ty;
    });
    return map;
  }, [types]);

  const load = () => {
    setLoading(true);
    setError("");
    Promise.all([
      api.fetchHealthTrackTypes(),
      api.fetchHealthRecords(),
      api.fetchAccountPreferences(),
    ])
      .then(([typesRes, recordsRes, prefsRes]) => {
        setTypes(typesRes.data.types || []);
        setRecords(recordsRes.data.records || []);
        setUnitsPref(prefsRes.data.units || "metric");
      })
      .catch((err) => {
        setError(err?.response?.data?.error || "Could not load your tracking data.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Layout>
        <div className="tracking-wrapper w-full mb-10">
          <div className="main-wrapper w-full">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-26 font-bold text-dark-gray dark:text-white">
                Health Tracking
              </h1>
              {types.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAddModal(true)}
                  className="rounded-[50px] text-base text-white font-bold px-6 py-3 bg-purple"
                >
                  + Add Reading
                </button>
              )}
            </div>

            {error && <p className="text-center text-red-700 mb-4">{error}</p>}

            <div className="update-table w-full p-8 bg-white dark:bg-dark-white   overflow-hidden rounded-2xl section-shadow min-h-[300px]">
              {loading ? (
                <p className="text-center text-thin-light-gray py-16">Loading…</p>
              ) : records.length === 0 ? (
                <div className="flex flex-col items-center py-16">
                  <p className="text-thin-light-gray mb-6">No readings logged yet.</p>
                  <button
                    type="button"
                    onClick={() => setShowAddModal(true)}
                    className="btn-login rounded-[50px] text-white font-bold px-8 py-3 bg-purple"
                  >
                    Get Started
                  </button>
                </div>
              ) : (
                <div className="relative w-full overflow-x-auto sm:rounded-lg">
                  <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                    <tbody>
                      <tr className="text-base text-thin-light-gray whitespace-nowrap border-b dark:border-[#5356fb29]">
                        <td className="py-4">Type</td>
                        <td className="py-4 text-center">Value</td>
                        <td className="py-4 text-right">Date</td>
                      </tr>
                      {records.map((record) => {
                        const type = typesById[record.type];
                        return (
                          <tr
                            key={record.id}
                            onClick={() => setDetailRecord(record)}
                            className="cursor-pointer bg-white dark:bg-dark-white   border-b dark:border-[#5356fb29]  hover:bg-gray-50"
                          >
                            <td className="py-4">
                              <h1 className="font-bold text-lg text-dark-gray dark:text-white whitespace-nowrap">
                                {type ? type.label : record.type}
                              </h1>
                            </td>
                            <td className="text-center py-4 px-2">
                              <span className="text-base text-purple font-medium whitespace-nowrap">
                                {formatValues(record, type)} {record.unit}
                              </span>
                            </td>
                            <td className="text-right py-4 px-2">
                              <span className="text-sm text-thin-light-gray whitespace-nowrap">
                                {formatWhen(record.recorded_at)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>

      {showAddModal && (
        <ModalCom action={() => setShowAddModal(false)} situation={showAddModal}>
          <AddHealthRecordModal
            types={types}
            unitsPref={unitsPref}
            onClose={() => setShowAddModal(false)}
            onSaved={(record) => {
              setRecords((prev) => [record, ...prev]);
              setShowAddModal(false);
            }}
          />
        </ModalCom>
      )}

      {detailRecord && (
        <ModalCom action={() => setDetailRecord(null)} situation={!!detailRecord}>
          <HealthRecordDetailModal
            record={detailRecord}
            type={typesById[detailRecord.type]}
            allRecordsOfType={records.filter((r) => r.type === detailRecord.type)}
            onClose={() => setDetailRecord(null)}
            onDeleted={(id) => {
              setRecords((prev) => prev.filter((r) => r.id !== id));
              setDetailRecord(null);
            }}
          />
        </ModalCom>
      )}
    </>
  );
}
